import { TILE_SIZE } from '../main.js';

/**
 * Manages combat interactions between player and enemies.
 */
export class CombatSystem {
  constructor(eventBus, damageNumbers, statusEffectSystem) {
    this.eventBus = eventBus;
    this.damageNumbers = damageNumbers;
    this.statusEffectSystem = statusEffectSystem || null;

    // Attack hitbox dimensions
    this.attackWidth = 20;
    this.attackHeight = 14;

    // Track which enemies were already hit in current swing
    this.hitEnemies = new Set();
    this.isAttackActive = false;
  }

  /**
   * Perform player attack: creates a hitbox in facing direction and checks enemies.
   * @param {Player} player - The player entity
   * @param {Enemy[]} enemies - Array of alive enemies
   * @returns {Enemy[]} Array of enemies that were hit
   */
  playerAttack(player, enemies) {
    const hits = [];

    // Calculate attack hitbox position based on facing direction
    const playerCenterX = player.x + TILE_SIZE / 2;
    const playerCenterY = player.y + TILE_SIZE / 2;

    // Normalize facing direction
    const fx = player.facingX;
    const fy = player.facingY;
    const len = Math.sqrt(fx * fx + fy * fy);
    if (len === 0) return hits;
    const nx = fx / len;
    const ny = fy / len;

    // Offset hitbox in facing direction (use weapon range if available)
    const attackRange = player.getAttackRange ? player.getAttackRange() : 20;
    const hitboxOffset = Math.max(12, attackRange * 0.55);
    const hitboxCenterX = playerCenterX + nx * hitboxOffset;
    const hitboxCenterY = playerCenterY + ny * hitboxOffset;

    const hitboxLeft = hitboxCenterX - this.attackWidth / 2;
    const hitboxTop = hitboxCenterY - this.attackHeight / 2;
    const hitboxRight = hitboxLeft + this.attackWidth;
    const hitboxBottom = hitboxTop + this.attackHeight;

    for (const enemy of enemies) {
      if (!enemy.isAlive || enemy.state === 'dead') continue;
      if (this.hitEnemies.has(enemy)) continue;

      // Enemy bounding box
      const eOffset = (TILE_SIZE - enemy.size) / 2;
      const eLeft = enemy.x + eOffset;
      const eTop = enemy.y + eOffset;
      const eRight = eLeft + enemy.size;
      const eBottom = eTop + enemy.size;

      // AABB collision
      if (
        hitboxLeft < eRight &&
        hitboxRight > eLeft &&
        hitboxTop < eBottom &&
        hitboxBottom > eTop
      ) {
        // Use weapon damage range if available, otherwise default 10-15
        const [minDmg, maxDmg] = player.getDamageRange ? player.getDamageRange() : [10, 15];
        let damage = minDmg + Math.floor(Math.random() * (maxDmg - minDmg + 1));

        // Frozen bonus: +50% damage, then clear frozen (M18)
        if (this.statusEffectSystem && this.statusEffectSystem.isFrozen(enemy)) {
          damage = Math.round(damage * 1.5);
          this.statusEffectSystem.remove(enemy, 'frozen');
        }

        // Knockback direction: from player to enemy
        const eCenterX = enemy.x + TILE_SIZE / 2;
        const eCenterY = enemy.y + TILE_SIZE / 2;
        const kbDx = eCenterX - playerCenterX;
        const kbDy = eCenterY - playerCenterY;
        const kbLen = Math.sqrt(kbDx * kbDx + kbDy * kbDy);
        const kbNx = kbLen > 0 ? kbDx / kbLen : nx;
        const kbNy = kbLen > 0 ? kbDy / kbLen : ny;

        enemy.takeDamage(damage, kbNx, kbNy);
        this.hitEnemies.add(enemy);

        // Spawn damage number
        this.damageNumbers.spawn(eCenterX, eCenterY - 8, damage, 0xffffff);

        hits.push(enemy);
      }
    }

    return hits;
  }

  /**
   * Check if an enemy can attack the player (proximity check).
   * @param {Enemy} enemy - The attacking enemy
   * @param {Player} player - The player
   * @returns {boolean} True if damage was dealt
   */
  enemyAttack(enemy, player) {
    if (!enemy.isAlive || enemy.state !== 'attack') return false;
    if (enemy.attackCooldown > 0) return false;

    const eCenterX = enemy.x + TILE_SIZE / 2;
    const eCenterY = enemy.y + TILE_SIZE / 2;
    const pCenterX = player.x + TILE_SIZE / 2;
    const pCenterY = player.y + TILE_SIZE / 2;

    const dx = pCenterX - eCenterX;
    const dy = pCenterY - eCenterY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const attackRange = 1.5 * TILE_SIZE;
    if (dist <= attackRange) {
      // Pass enemy center position for knockback direction calculation
      const wasInvuln = player.isInvulnerable();
      player.takeDamage(enemy.damage, eCenterX, eCenterY);
      enemy.attackCooldown = 1.5;

      // Spawn damage number on player (red) -- only if not invulnerable
      if (!wasInvuln) {
        this.damageNumbers.spawn(pCenterX, pCenterY - 8, enemy.damage, 0xff4444);
      }
      return true;
    }

    return false;
  }

  /**
   * Begin a new attack swing (resets hit tracking).
   */
  beginSwing() {
    this.hitEnemies.clear();
    this.isAttackActive = true;
  }

  /**
   * End the current attack swing.
   */
  endSwing() {
    this.hitEnemies.clear();
    this.isAttackActive = false;
  }
}
